import {
  Code,
  ConnectError,
  createClient,
  type Interceptor,
} from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";
import { AdminService } from "@api-gen/admin_pb";
import { PaymentService } from "@api-gen/payment_pb";
import { LiveService } from "@api-gen/live_pb";
import { SportsService } from "@api-gen/sports_pb";
import { ContentService } from "@api-gen/content_pb";
import { ChatService } from "@api-gen/chat_pb";
import {
  adminDeviceFingerprint,
  consoleLoginRealm,
  consoleRouterBase,
  consoleStorageKey,
} from "@/console-domain";

const publicAdminMethods = [
  "/AdminLogin",
  "/VerifyAdminMfa",
  "/RefreshAdminSession",
];
const productionConsolePrefixes = new Set([
  "root",
  "ops",
  "admin",
  "finance",
  "creator",
]);

export function defaultAdminApiBaseUrl(
  hostname = window.location.hostname,
  origin = window.location.origin,
) {
  const normalizedHost = hostname.trim().toLowerCase();
  const labels = normalizedHost.split(".");
  if (labels.length >= 3 && productionConsolePrefixes.has(labels[0] ?? "")) {
    const protocol = origin.startsWith("http://") ? "http" : "https";
    return `${protocol}://live-api.${labels.slice(1).join(".")}`;
  }
  return origin;
}

export function isPublicAdminRequest(url: string) {
  return publicAdminMethods.some((method) => url.endsWith(method));
}

const authentication: Interceptor = (next) => async (request) => {
  const publicRequest = isPublicAdminRequest(request.url);
  if (!publicRequest) {
    const token = sessionStorage.getItem(consoleStorageKey("accessToken"));
    if (token) request.header.set("Authorization", `Bearer ${token}`);
  }
  try {
    return await next(request);
  } catch (error) {
    if (
      !publicRequest &&
      ConnectError.from(error).code === Code.Unauthenticated
    ) {
      try {
        const token = await refreshAdminAccessToken();
        request.header.set("Authorization", `Bearer ${token}`);
        return await next(request);
      } catch {
        clearAdminBrowserSession();
        const loginPath = `${consoleRouterBase()}login`;
        if (window.location.pathname !== loginPath) {
          window.location.replace(loginPath);
        }
      }
    }
    throw error;
  }
};

export const adminApiBaseUrl =
  import.meta.env.VITE_API_BASE_URL ?? defaultAdminApiBaseUrl();
const credentialedFetch: typeof globalThis.fetch = (input, init) =>
  globalThis.fetch(input, { ...init, credentials: "include" });
const refreshTransport = createConnectTransport({
  baseUrl: adminApiBaseUrl,
  fetch: credentialedFetch,
});
const refreshClient = createClient(AdminService, refreshTransport);
let refreshInFlight: Promise<string> | undefined;

async function refreshAdminAccessToken() {
  if (!refreshInFlight) {
    refreshInFlight = refreshClient
      .refreshAdminSession({
        deviceFingerprint: adminDeviceFingerprint(),
        domain: consoleLoginRealm(),
      })
      .then((response) => {
        if (!response.accessToken || !response.profile) {
          throw new Error("管理员会话刷新响应不完整");
        }
        sessionStorage.setItem(
          consoleStorageKey("accessToken"),
          response.accessToken,
        );
        sessionStorage.setItem(
          consoleStorageKey("profile"),
          JSON.stringify(response.profile),
        );
        return response.accessToken;
      })
      .finally(() => {
        refreshInFlight = undefined;
      });
  }
  return refreshInFlight;
}

function clearAdminBrowserSession() {
  sessionStorage.removeItem(consoleStorageKey("accessToken"));
  sessionStorage.removeItem(consoleStorageKey("profile"));
}

const transport = createConnectTransport({
  baseUrl: adminApiBaseUrl,
  fetch: credentialedFetch,
  interceptors: [authentication],
});

export const api = {
  admin: createClient(AdminService, transport),
  payment: createClient(PaymentService, transport),
  live: createClient(LiveService, transport),
  sports: createClient(SportsService, transport),
  content: createClient(ContentService, transport),
  chat: createClient(ChatService, transport),
};

export function readableError(error: unknown) {
  return (error instanceof Error ? error.message : String(error)).replace(
    /^\[[^\]]+\]\s*/,
    "",
  );
}

export function formatMoney(amount: bigint | number, currency = "CNY") {
  return new Intl.NumberFormat("zh-CN", { style: "currency", currency }).format(
    Number(amount) / 100,
  );
}
